import type { Surah } from '@/types/domain';
import { Button } from '@/components/ui/Button';

/**
 * Shown when the user has stepped past the end ayah of the session.
 *
 * Summarizes what they marked memorized, offers a way to start a new
 * session or end this one. Calm tone — this is supposed to feel like
 * a closing breath, not a victory parade.
 */

type SessionCompleteProps = {
  surah: Surah;
  startAyah: number;
  endAyah: number;
  memorizedCount: number;
  onStartNew: () => void;
  onReviewAgain: () => void;
};

export function SessionComplete({
  surah,
  startAyah,
  endAyah,
  memorizedCount,
  onStartNew,
  onReviewAgain,
}: SessionCompleteProps) {
  const total = endAyah - startAyah + 1;
  const pct = total > 0 ? Math.round((memorizedCount / total) * 100) : 0;

  return (
    <section className="container-prose pt-16 sm:pt-24 pb-24 text-center">
      <p className="text-xs uppercase tracking-[0.22em] text-accent-600 dark:text-accent-400 mb-3">
        Session complete
      </p>
      <h1 className="font-serif text-display-2 text-ink-900 dark:text-ink-50 mb-3">
        {surah.name_en}
      </h1>
      <p className="text-base text-ink-600 dark:text-ink-300 leading-relaxed mb-10">
        You worked through ayahs {startAyah}–{endAyah} ({total} total) and
        marked {memorizedCount} as memorized — {pct}%.
      </p>

      <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
        <Button variant="primary" size="lg" onClick={onReviewAgain}>
          Review this range again
        </Button>
        <Button variant="secondary" size="lg" onClick={onStartNew}>
          Start a new session
        </Button>
      </div>

      <p className="mt-10 text-xs text-ink-500 dark:text-ink-400 leading-relaxed">
        Progress is saved locally in your browser. Future versions will
        sync to your account so you can revise across devices.
      </p>
    </section>
  );
}
